import React, { useMemo } from "react";
import { BRAND } from "../../lib/brand";

// Real coordinates of the Central Alberta towns we service.
const CITIES = [
  { name: "Lacombe", lat: 52.4683, lon: -113.7369, hub: true },
  { name: "Red Deer", lat: 52.2681, lon: -113.8112 },
  { name: "Ponoka", lat: 52.6772, lon: -113.5817 },
  { name: "Rimbey", lat: 52.6300, lon: -114.2350 },
  { name: "Wetaskiwin", lat: 52.9694, lon: -113.3769 },
  { name: "Sylvan Lake", lat: 52.3097, lon: -114.0956 },
  { name: "Blackfalds", lat: 52.3833, lon: -113.7900 },
  { name: "Bashaw", lat: 52.5772, lon: -112.9700 },
];

const W = 440;
const H = 340;
const PAD = 60;

export const ServiceArea = () => {
  const { pts, hub, ring } = useMemo(() => {
    const lats = CITIES.map((c) => c.lat);
    const lons = CITIES.map((c) => c.lon);
    const latMin = Math.min(...lats), latMax = Math.max(...lats);
    const lonMin = Math.min(...lons), lonMax = Math.max(...lons);
    const px = (c) => ({
      ...c,
      x: PAD + ((c.lon - lonMin) / (lonMax - lonMin)) * (W - 2 * PAD),
      y: PAD + ((latMax - c.lat) / (latMax - latMin)) * (H - 2 * PAD),
    });
    const pts = CITIES.map(px);
    const hub = pts.find((p) => p.hub);
    const ring = Math.max(...pts.map((p) => Math.hypot(p.x - hub.x, p.y - hub.y))) + 26;
    return { pts, hub, ring };
  }, []);

  return (
    <section id="area" className="bg-[#0a0a0a] section-divider">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-[#7CB518]">// Where We Roll</span>
          <h2 className="font-display uppercase text-4xl md:text-6xl mt-2 leading-none">
            Service <span className="text-[#7CB518]">Area</span>
          </h2>
          <p className="mt-4 text-sm text-white/70 max-w-md">
            Proudly based in Central Alberta. We serve the <span className="text-[#7CB518] font-bold">Central
            Alberta area</span> — if you're nearby and not sure, just ask.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {BRAND.towns.map((t) => (
              <span key={t} data-testid={`town-${t}`} className="border-2 border-[#2d2d2d] px-3 py-1 text-xs uppercase tracking-widest hover:border-[#7CB518] transition-colors">
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="relative border-2 border-[#7CB518] bg-[#0d0d0d] aspect-[4/3] overflow-hidden hard-shadow" data-testid="service-area-map">
          <div className="absolute top-3 left-3 z-10 flex items-center gap-2 bg-black/80 border-2 border-[#7CB518] px-3 py-1.5">
            <span className="font-display uppercase text-sm">Central Alberta</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/50">Our Service Area</span>
          </div>

          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="xMidYMid slice">
            <rect width={W} height={H} fill="#0d0d0d" />
            {/* faint grid */}
            {[...Array(9)].map((_, i) => (
              <line key={`v${i}`} x1={(W / 8) * i} y1="0" x2={(W / 8) * i} y2={H} stroke="#ffffff" strokeOpacity="0.04" />
            ))}
            {[...Array(7)].map((_, i) => (
              <line key={`h${i}`} x1="0" y1={(H / 6) * i} x2={W} y2={(H / 6) * i} stroke="#ffffff" strokeOpacity="0.04" />
            ))}

            {/* coverage ring around hub */}
            <circle cx={hub.x} cy={hub.y} r={ring} fill="#7CB518" fillOpacity="0.06" stroke="#7CB518" strokeOpacity="0.6" strokeWidth="2" strokeDasharray="6 6" />

            {/* routes from hub to each town */}
            {pts.filter((p) => !p.hub).map((p) => (
              <line key={`r-${p.name}`} x1={hub.x} y1={hub.y} x2={p.x} y2={p.y} stroke="#7CB518" strokeOpacity="0.35" strokeWidth="1.5" />
            ))}

            {/* town markers */}
            {pts.map((p) => {
              const anchorEnd = p.x > W * 0.72;
              const tx = anchorEnd ? p.x - 8 : p.x + 8;
              return (
                <g key={p.name}>
                  {p.hub ? (
                    <>
                      <circle cx={p.x} cy={p.y} r="7" fill="#7CB518" />
                      <circle cx={p.x} cy={p.y} r="12" fill="none" stroke="#7CB518" strokeWidth="2" />
                    </>
                  ) : (
                    <circle cx={p.x} cy={p.y} r="4.5" fill="#0a0a0a" stroke="#7CB518" strokeWidth="2" />
                  )}
                  <text
                    x={tx}
                    y={p.y + 4}
                    textAnchor={anchorEnd ? "end" : "start"}
                    fill={p.hub ? "#7CB518" : "#ffffff"}
                    fontFamily="'IBM Plex Mono', monospace"
                    fontSize={p.hub ? "15" : "12"}
                    fontWeight={p.hub ? "700" : "500"}
                    style={{ textTransform: "uppercase", paintOrder: "stroke", stroke: "#0a0a0a", strokeWidth: 3, strokeLinejoin: "round" }}
                  >
                    {p.name}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </section>
  );
};
